/**
 * Marquee infinito Belabelly — loop contínuo sem vãos (cards, ticker, promo trust).
 */
(function () {
  /** @type {WeakSet<HTMLElement>} */
  const initializedTracks = new WeakSet();

  /**
   * @param {HTMLElement} track
   */
  function setupInfiniteLoop(track) {
    if (!track) return;

    const groupSelector = track.dataset.marqueeGroup || '.vf-marquee__group';
    const viewport = track.parentElement;

    if (!track.dataset.speed) {
      const host = track.parentElement;
      if (host) {
        const styles = getComputedStyle(host);
        const duration = parseFloat(styles.getPropertyValue('--marquee-duration'));
        const durationMobile = parseFloat(styles.getPropertyValue('--marquee-duration-mobile'));
        if (duration) track.dataset.speed = String(duration);
        if (durationMobile) track.dataset.speedMobile = String(durationMobile);
      }
    }

    if (track.hasAttribute('data-vf-css-marquee')) {
      track.removeAttribute('data-vf-css-marquee');
      track.setAttribute('data-vf-infinite-marquee', '');
    }

    track.querySelectorAll(`${groupSelector}:not([data-vf-loop-clone])`).forEach((node, index) => {
      if (index > 0) node.remove();
    });

    const group = track.querySelector(`${groupSelector}:not([data-vf-loop-clone])`);
    if (!group || !viewport) return;
    if (initializedTracks.has(track)) return;
    initializedTracks.add(track);

    /** @type {number | null} */
    let debounceTimer = null;
    /** @type {number | null} */
    let retryTimer = null;
    let retryCount = 0;
    let lastShift = 0;
    let lastCopies = 0;

    const syncClones = () => {
      track.querySelectorAll('[data-vf-loop-clone]').forEach((node) => node.remove());

      const groupWidth = Math.ceil(group.getBoundingClientRect().width);
      const viewWidth = Math.ceil(viewport.getBoundingClientRect().width);
      if (!groupWidth || !viewWidth) return null;

      const minCopies = Math.max(2, Math.ceil((viewWidth + groupWidth) / groupWidth) + 1);

      for (let i = 1; i < minCopies; i += 1) {
        const clone = group.cloneNode(true);
        clone.setAttribute('data-vf-loop-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      }

      return { groupWidth, minCopies };
    };

    const scheduleRetry = () => {
      if (track.classList.contains('is-animating')) return;
      if (retryCount >= 12) return;

      retryCount += 1;
      if (retryTimer) window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        window.requestAnimationFrame(applyMetrics);
      }, Math.min(150 * retryCount, 1200));
    };

    const applyMetrics = () => {
      const metrics = syncClones();
      if (!metrics) {
        scheduleRetry();
        return;
      }

      const { groupWidth, minCopies } = metrics;
      const nextGroup = group.nextElementSibling;
      const groupStep =
        nextGroup instanceof HTMLElement
          ? Math.ceil(nextGroup.getBoundingClientRect().left - group.getBoundingClientRect().left)
          : groupWidth;
      const changed = groupStep !== lastShift || minCopies !== lastCopies;
      lastShift = groupStep;
      lastCopies = minCopies;

      track.style.setProperty('--marquee-shift', `${groupStep}px`);

      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      track.classList.toggle('vf-marquee--mobile', isMobile);

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const speedSource =
        isMobile && track.dataset.speedMobile ? track.dataset.speedMobile : track.dataset.speed || '16';
      const seconds = parseFloat(speedSource);
      track.style.setProperty('--marquee-duration', `${Math.max(seconds, 4)}s`);

      if (reducedMotion) {
        track.classList.remove('is-animating');
        return;
      }

      if (!changed && track.classList.contains('is-animating')) return;

      track.classList.remove('is-animating');
      void track.offsetWidth;
      track.classList.add('is-animating');
    };

    const scheduleApply = () => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        window.requestAnimationFrame(applyMetrics);
      }, 80);
    };

    applyMetrics();
    scheduleRetry();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(scheduleApply);
      observer.observe(group);
      observer.observe(viewport);
    } else {
      window.addEventListener('resize', scheduleApply);
    }

    const mobileQuery = window.matchMedia('(max-width: 989px)');
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onBreakpointChange = () => scheduleApply();
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', onBreakpointChange);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(onBreakpointChange);
    }
    if (typeof motionQuery.addEventListener === 'function') {
      motionQuery.addEventListener('change', onBreakpointChange);
    } else if (typeof motionQuery.addListener === 'function') {
      motionQuery.addListener(onBreakpointChange);
    }
    window.addEventListener('orientationchange', scheduleApply);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleApply).catch(() => {});
    }

    track.querySelectorAll('img, video').forEach((media) => {
      if (!(media instanceof HTMLImageElement || media instanceof HTMLVideoElement)) return;
      if (media instanceof HTMLImageElement && media.complete) return;
      media.addEventListener('loadeddata', scheduleApply, { once: true });
      media.addEventListener('load', scheduleApply, { once: true });
    });

    if (typeof IntersectionObserver !== 'undefined') {
      const visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) scheduleApply();
          });
        },
        { threshold: 0.01 }
      );
      visibilityObserver.observe(track);
    }
  }

  /**
   * @param {ParentNode} [root]
   */
  function initAll(root = document) {
    root.querySelectorAll('[data-vf-infinite-marquee], [data-vf-css-marquee]').forEach(setupInfiniteLoop);
  }

  function boot() {
    initAll();
    window.addEventListener('load', () => initAll());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) {
      initAll(event.target);
    }
  });
})();
